import { FormEvent, useEffect, useState } from "react";
import { useParams } from "@tanstack/react-router";
import { Loader2, Send } from "lucide-react";
import { Badge, Button, Card, PageHeader } from "@/components/primitives";
import {
  ConsultantCandidate,
  CourseQuestionResponse,
  useCosmosClient
} from "@/lib/cosmos-api";

export function CourseDetailPage() {
  const { courseId } = useParams({ from: "/courses/$courseId" });
  const cosmos = useCosmosClient();
  const [course, setCourse] = useState<ConsultantCandidate | null>(null);
  const [prompt, setPrompt] = useState("");
  const [answer, setAnswer] = useState<CourseQuestionResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    void cosmos
      .getCourse(courseId)
      .then(setCourse)
      .catch((err) =>
        setError(err instanceof Error ? err.message : "Failed to load course")
      );
  }, [cosmos, courseId]);

  const ask = async (event: FormEvent) => {
    event.preventDefault();
    if (!prompt.trim()) return;
    setLoading(true);
    setError(null);
    try {
      setAnswer(await cosmos.askCourse(courseId, { prompt }));
      setPrompt("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to ask Cosmos");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page">
      <PageHeader
        eyebrow="Course"
        title={course?.title ?? courseId}
        subtitle={course?.description ?? "Loading course profile..."}
        actions={course?.difficulty && <Badge tone="ai">{course.difficulty}</Badge>}
      />
      {error && <Card className="error-card">{error}</Card>}
      {course && (
        <Card>
          <div className="inline-list">
            {course.domainTags.map((tag) => (
              <Badge key={tag}>{tag}</Badge>
            ))}
            {course.credentialOffered && <Badge tone="success">credential</Badge>}
            {course.durationMinutes && <Badge>{course.durationMinutes} min</Badge>}
          </div>
        </Card>
      )}
      <Card>
        <h2>Ask Cosmos about this course</h2>
        <form className="prompt-form" onSubmit={(event) => void ask(event)}>
          <textarea
            value={prompt}
            onChange={(event) => setPrompt(event.target.value)}
            placeholder="What skills does this course build? Who is it best for?"
          />
          <Button disabled={loading || !prompt.trim()}>
            {loading ? <Loader2 className="spin" size={16} /> : <Send size={16} />}
            Ask course
          </Button>
        </form>
      </Card>
      {answer && (
        <Card>
          <div className="card-header">
            <h2>Cosmos answer</h2>
            <Badge tone="ai">{answer.modelRouting.model}</Badge>
          </div>
          <p>{answer.answer}</p>
          <h3>Citations</h3>
          <ul>
            {answer.citations.map((source) => (
              <li key={source.sourceId}>
                {source.title} ({Math.round(source.confidence * 100)}%)
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
